import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { connectAuthPost, normalizeMeetup, statusLabel, formatWhen } from './connectApi';
import './styles/ConnectMeet.css';

/**
 * One meetup: who is hosting, when, and the controls that match your
 * relationship to it.
 *
 * The participant list and headcount only load for people who are actually
 * in it — the server decides that, this screen just renders what it is given.
 */
export default function MeetupDetail({ meetupId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [meetup, setMeetup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await connectAuthPost('connect/meetups/get', { meetup_id: meetupId });
      if (res.success) setMeetup(normalizeMeetup(res.data.meetup));
      else setNotice(res.response || 'Meetup not found.');
    } catch (err) {
      setNotice('Could not reach the server.');
    } finally {
      setIsLoading(false);
    }
  }, [meetupId]);

  useEffect(() => { load(); }, [load]);

  const act = async (endpoint, payload, successNotice) => {
    setBusy(true);
    setNotice('');
    try {
      const res = await connectAuthPost(endpoint, { meetup_id: meetupId, ...payload });
      setNotice(res.response || successNotice || '');
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setNotice('Could not reach the server.');
    } finally {
      setBusy(false);
    }
  };

  const openChat = () => {
    if (meetup && meetup.conversationId) {
      navigate(`/connect/messages?conversation=${meetup.conversationId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="meet-overlay" onClick={onClose}>
        <div className="meet-modal" onClick={(e) => e.stopPropagation()}>
          <div className="meet-modal-body"><p>Loading…</p></div>
        </div>
      </div>
    );
  }

  if (!meetup) {
    return (
      <div className="meet-overlay" onClick={onClose}>
        <div className="meet-modal" onClick={(e) => e.stopPropagation()}>
          <div className="meet-modal-head">
            <h2>Meetup</h2>
            <button className="meet-modal-close" onClick={onClose}>×</button>
          </div>
          <div className="meet-modal-body"><p>{notice || 'Not found.'}</p></div>
        </div>
      </div>
    );
  }

  const pending = (meetup.participants || []).filter((p) => p.status === 'pending');
  const going = (meetup.participants || []).filter((p) => p.status === 'approved');
  const isIn = meetup.myStatus === 'approved' || meetup.isHost;

  return (
    <div className="meet-overlay" onClick={onClose}>
      <div className="meet-modal" onClick={(e) => e.stopPropagation()}>
        <div className="meet-modal-head">
          <h2>{meetup.title}</h2>
          <button className="meet-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="meet-modal-body">
          <div className="meet-detail-meta">
            {meetup.activity && <span className="meet-card-activity">{meetup.activity}</span>}
            {meetup.alcoholFree && <span className="meet-card-flag">Alcohol-free</span>}
            {meetup.visibility === 'invite_only' && <span className="meet-card-flag">Invite only</span>}
          </div>

          <p className="meet-detail-when">{formatWhen(meetup.startAt)} – {formatWhen(meetup.endAt).split(', ')[1]}</p>
          {meetup.area && <p className="meet-detail-area">Around {meetup.area}</p>}
          {meetup.description && <p className="meet-detail-desc">{meetup.description}</p>}

          <div className="meet-detail-host">
            <div className="meet-person-avatar">
              {meetup.host.profile_picture
                ? <img src={meetup.host.profile_picture} alt={meetup.host.full_name} />
                : <span>{(meetup.host.full_name || '?').charAt(0).toUpperCase()}</span>}
            </div>
            <div>
              <strong>{meetup.host.full_name}</strong>
              <span className="meet-person-status">{statusLabel(meetup.host.status)} · host</span>
            </div>
          </div>

          <p className="meet-detail-spaces">
            {meetup.isFull ? 'This meetup is full' : `${meetup.spacesLeft} of ${meetup.capacity} spaces left`}
            {meetup.requiresApproval && ' · the host approves who joins'}
          </p>

          {/* Only people in the meetup see who else is */}
          {isIn && going.length > 0 && (
            <>
              <h3 className="meet-detail-sub">Going</h3>
              <div className="meet-detail-people">
                {going.map((p) => (
                  <div key={p.id} className="meet-detail-person">
                    <div className="meet-person-avatar small">
                      {p.profile_picture
                        ? <img src={p.profile_picture} alt={p.full_name} />
                        : <span>{p.full_name.charAt(0).toUpperCase()}</span>}
                    </div>
                    <span>{p.full_name}{p.is_host ? ' · host' : ''}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {meetup.isHost && pending.length > 0 && (
            <>
              <h3 className="meet-detail-sub">Requests</h3>
              <div className="meet-detail-people">
                {pending.map((p) => (
                  <div key={p.id} className="meet-detail-person request">
                    <div className="meet-person-avatar small">
                      {p.profile_picture
                        ? <img src={p.profile_picture} alt={p.full_name} />
                        : <span>{p.full_name.charAt(0).toUpperCase()}</span>}
                    </div>
                    <span>{p.full_name}</span>
                    <div className="meet-detail-actions">
                      <button className="meet-btn primary small" disabled={busy}
                              onClick={() => act('connect/meetups/respond', { user_id: p.id, decision: 'approve' })}>
                        Approve
                      </button>
                      <button className="meet-btn ghost small" disabled={busy}
                              onClick={() => act('connect/meetups/respond', { user_id: p.id, decision: 'decline' })}>
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {notice && <div className="meet-notice">{notice}</div>}
        </div>

        <div className="meet-modal-foot">
          {isIn && meetup.conversationId && (
            <button className="meet-btn ghost" onClick={openChat}>Open chat</button>
          )}

          {meetup.isHost ? (
            <button className="meet-btn danger" disabled={busy}
                    onClick={() => act('connect/meetups/cancel', {}, 'Meetup cancelled.')}>
              Cancel meetup
            </button>
          ) : meetup.myStatus === 'approved' ? (
            <button className="meet-btn ghost" disabled={busy}
                    onClick={() => act('connect/meetups/leave', {}, 'You left.')}>
              Leave
            </button>
          ) : meetup.myStatus === 'pending' ? (
            <button className="meet-btn ghost" disabled>Request sent</button>
          ) : (
            <button className="meet-btn primary" disabled={busy || meetup.isFull}
                    onClick={() => act('connect/meetups/join', {})}>
              {meetup.isFull ? 'Full' : meetup.requiresApproval ? 'Ask to join' : 'Join'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
